import _ from "lodash";
import { getGeoLocation, getPropertySearch } from "@/services/commonServices";
import { matchMlsListingByAddress } from "@/services/v2/mlsListings";
import { getNiceToHaves } from "@/services/commonServices";
import {
  matchNiceToHaves,
  normalizeFeatureText,
} from "@/helpers/nice2HaveMatch";
import { INVENTORY_MAP_RADIUS, INVENTORY_SELL } from "@/constants/common";

const pickComponent = (components, type, key = "short_name") =>
  _.get(
    (components || []).find((c) => (c.types || []).includes(type)),
    key,
    "",
  );

// A property is a building at an address. Google returns the same shape for
// "Carmel, IN" as it does for "1042 Rangeline Rd", so the street number and
// route are what separate a property from a place - without them the record
// has nothing for the matching engine to score and nothing to show an agent.
export const isStreetAddress = (geocodeResult) => {
  const components = _.get(geocodeResult, "address_components", []);
  const types = _.get(geocodeResult, "types", []);
  if (types.includes("locality") || types.includes("postal_code")) return false;
  return (
    !!pickComponent(components, "street_number") &&
    !!pickComponent(components, "route")
  );
};

export const CITY_NOT_A_PROPERTY_MESSAGE =
  "Enter a full street address. A city or area on its own is not a property.";

export const geocodeAddress = async (value) => {
  const query = `address=${encodeURIComponent(value)}&components=country:US`;
  const data = await getGeoLocation(query);
  const results = _.get(data, "results", []);
  return results.length > 0 ? results[0] : null;
};

export const describeAddress = (geocodeResult) => {
  const components = _.get(geocodeResult, "address_components", []);
  const streetNumber = pickComponent(components, "street_number");
  const route = pickComponent(components, "route", "long_name");
  return {
    locAddress: _.get(geocodeResult, "formatted_address", "").replace(
      /, USA$/,
      "",
    ),
    street: [streetNumber, route].filter(Boolean).join(" "),
    streetNumber,
    route,
    city: pickComponent(components, "locality", "long_name"),
    county: pickComponent(components, "administrative_area_level_2"),
    state: pickComponent(components, "administrative_area_level_1"),
    zip: pickComponent(components, "postal_code"),
    lat: _.get(geocodeResult, "geometry.location.lat", null),
    lng: _.get(geocodeResult, "geometry.location.lng", null),
  };
};

// Zillow/Apify homeType strings mapped onto PropertyTypes ids, so the
// confirmation opens with a type already chosen. A wrong guess the agent can
// correct in one click beats an empty select they have to reason about.
const HOME_TYPE_TO_PROPERTY_TYPE = {
  SINGLE_FAMILY: 1,
  SINGLEFAMILY: 1,
  HOUSE: 1,
  CONDO: 2,
  CONDOMINIUM: 2,
  APARTMENT: 2,
  TOWNHOUSE: 3,
  TOWNHOME: 3,
  MULTI_FAMILY: 4,
  MULTIFAMILY: 4,
  MANUFACTURED: 5,
  MOBILE: 5,
  LOT: 6,
  VACANT_LAND: 6,
  LAND: 6,
};

export const propertyTypeFromHomeType = (homeType) => {
  if (!homeType) return null;
  const key = String(homeType).toUpperCase().replace(/[\s-]+/g, "_");
  return HOME_TYPE_TO_PROPERTY_TYPE[key] ?? null;
};

// The MLS is the authority on a property in this market, so its answer wins.
// The public property API (Zillow, with the Apify actor behind it) fills the
// gaps - a prospects-inventory home has no MLS row at all.
//
// Both are fired together rather than in sequence. They are independent
// lookups against different upstreams, and running them one after the other
// made the agent wait for the sum of two network round trips before the
// confirmation could render anything.
// Neither provider is contractually fast, and one of them having no record of
// an address is exactly the case where it takes longest to say so. Past this
// the agent gets the form with whatever did arrive rather than a dead wait.
const LOOKUP_TIMEOUT_MS = 9000;

const withTimeout = (promise, label) =>
  Promise.race([
    promise,
    new Promise((resolve) =>
      setTimeout(() => {
        console.log(`Quick add: ${label} timed out after ${LOOKUP_TIMEOUT_MS}ms`);
        resolve(null);
      }, LOOKUP_TIMEOUT_MS),
    ),
  ]);

const isEmpty = (v) => v === undefined || v === null || v === "";

/**
 * Look the address up with both providers and report each one the moment it
 * lands, via onPartial.
 *
 * These used to be awaited together, so the form waited on the slower of the
 * two even when the other had answered in a second - the agent sat looking at
 * empty fields for the worst case, every time. They now settle independently.
 *
 * Arrival order is therefore not fixed, so precedence cannot be "whoever ran
 * first wins". The MLS record is authoritative and overwrites; the public
 * record only fills what is still blank, whichever way round they return.
 */
export const lookupPropertyFacts = async (
  addressParts,
  geocodeResult,
  onPartial,
) => {
  const facts = { source: null, values: {}, features: [] };
  const fromPublic = new Set();

  const report = () => {
    if (typeof onPartial === "function") {
      onPartial({ ...facts, values: { ...facts.values } });
    }
  };

  const applyMls = (mlsResponse) => {
    const data =
      _.get(mlsResponse, "data", null) || _.get(mlsResponse, "result", null);
    if (!data?.matched || !data?.basics) return;
    facts.source = "mls";
    facts.mlsListingKey = data.listingKey || null;
    Object.entries(data.basics).forEach(([key, value]) => {
      // Authoritative: overwrites a public guess, but never a real blank.
      if (!isEmpty(value)) facts.values[key] = value;
    });
  };

  const applyPublic = (searchResponse) => {
    const result = _.get(searchResponse, "result", null);
    if (!result) return;
    const lotSquareFeet = _.get(result, "lotSquareFeet", null);
    const publicValues = {
      prprtyType: propertyTypeFromHomeType(_.get(result, "propertyType", null)),
      noBedRm: _.get(result, "bedrooms", null),
      noBathRm: _.get(result, "bathrooms", null),
      sqFt: _.get(result, "squareFeet", null),
      yearBuilt: _.get(result, "yearBuilt", null),
      lotSize: lotSquareFeet
        ? Math.round((lotSquareFeet / 43560) * 100) / 100
        : null,
      neighborHood: _.get(result, "neighborhood.name", null),
      sellAskngPrice: _.get(result, "mlsActive", false)
        ? _.get(result, "mlsListingPrice", null)
        : _.get(result, "zestimate", null),
    };
    Object.entries(publicValues).forEach(([key, value]) => {
      if (isEmpty(value)) return;
      // Only fill a gap - unless the gap is one this provider filled itself,
      // which keeps it correct if the MLS answers second.
      if (isEmpty(facts.values[key]) || fromPublic.has(key)) {
        facts.values[key] = value;
        fromPublic.add(key);
      }
    });

    // The provider lists an amenity once per category it belongs to, so a
    // house with gas heat and a gas range comes back with "Natural Gas" twice.
    const features = _.get(result, "features", []);
    if (Array.isArray(features)) {
      const seen = new Set();
      facts.features = features.filter((f) => {
        const key = String(f || "")
          .trim()
          .toLowerCase();
        if (!key || seen.has(key)) return false;
        seen.add(key);
        return true;
      });
    }
    if (!facts.source) facts.source = "public";
  };

  const mlsLookup = withTimeout(
    matchMlsListingByAddress({
      address: addressParts.locAddress,
      streetNumber: addressParts.streetNumber,
      streetName: addressParts.route,
      city: addressParts.city,
      state: addressParts.state,
      zip: addressParts.zip,
      lat: addressParts.lat,
      lng: addressParts.lng,
    }).catch((error) => {
      console.log("Quick add: MLS lookup failed", error);
      return null;
    }),
    "MLS lookup",
  ).then((response) => {
    applyMls(response);
    report();
  });

  const publicLookup = withTimeout(
    getPropertySearch({
      latitude: addressParts.lat,
      longitude: addressParts.lng,
      radius: INVENTORY_MAP_RADIUS,
      mls_active: true,
      street: addressParts.street,
      city: addressParts.city,
      state: addressParts.state,
      zipcode: addressParts.zip,
    }).catch((error) => {
      // The public lookup is a convenience, not a requirement - a missing key
      // or a rate limit must still leave the agent with a usable form.
      console.log("Quick add: public property lookup failed", error);
      return null;
    }),
    "public property lookup",
  ).then((response) => {
    applyPublic(response);
    report();
  });

  await Promise.all([mlsLookup, publicLookup]);

  return facts;
};

// Map the free-text feature strings the property lookup returns onto the
// platform's own nice-to-have ids, using the same matcher the full
// create-inventory flow uses. Returns [] on any failure - a property is still
// perfectly addable without its amenities.
export const matchFeaturesToNiceToHaves = async (features) => {
  if (!Array.isArray(features) || !features.length) return [];
  try {
    const response = await getNiceToHaves(INVENTORY_SELL);
    const matched =
      matchNiceToHaves(features, _.get(response, "result", null)) || [];
    // Deduping the source strings is not enough: distinct strings collapse onto
    // the same nice-to-have, so "Gas" and "Natural Gas" both arrive here as one
    // id, so "Gas" and "Natural Gas" arrive here as one option.
    //
    // Deduping on tab+id would do nothing: matchNiceToHaves already guarantees
    // uniqueness on exactly that key. The repeats come from the same LABEL
    // existing as separate options under different tabs - two ids, one word on
    // screen - so a duplicate is only visible as a duplicate by its text.
    // Normalized with the matcher's own function so the comparison here and
    // the matching above agree on what counts as the same word.
    const seen = new Set();
    return matched.filter((m) => {
      const key = normalizeFeatureText(m.label) || `${m.tab}:${m.id}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  } catch (error) {
    console.log("Quick add: nice-to-have match failed", error);
    return [];
  }
};
